import React, { useState, useMemo } from "react";
import { Select, Tag, Popconfirm, message } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import { ButtonItem, SelectItem } from "../../../../Components/Items";
import RackEspacioModal from "./RackEspacioModal";

const { Option } = Select;

const r2 = (n) => Math.round(n * 100) / 100;

export const rackTieneMedidas = (rack) => rack && rack.anchoMetros > 0 && rack.altoMetros > 0 && rack.profundidadMetros > 0;

//Medidas de una ubicación en texto compacto "ancho × alto × largo m" 
export const medidasTexto = (u) => {
  if (!u) return null;
  if (!(u.ancho > 0 || u.alto > 0 || u.largo > 0)) return null;
  const v = (n) => (n > 0 ? r2(n) : "?");
  return `${v(u.ancho)} × ${v(u.alto)} × ${v(u.largo)} m`;
};

//Cuadrícula del rack (celdas iguales) y tamaño físico de cada celda derivado del tamaño total del rack.
export const dimensionesRack = (rack) => {
  const columnas = rack.numeroColumnas || 1;
  const niveles = rack.numeroNiveles || 1;
  const profundidades = rack.numeroProfundidades || 1;
  return {
    columnas,
    niveles,
    profundidades,
    celdaAncho: rack.anchoMetros > 0 ? rack.anchoMetros / columnas : null,
    celdaAlto: rack.altoMetros > 0 ? rack.altoMetros / niveles : null,
    celdaProf: rack.profundidadMetros > 0 ? rack.profundidadMetros / profundidades : null,
  };
};


export const spanDe = (u, dims) => ({
  spanC: dims.celdaAncho && u.ancho > 0 ? Math.max(1, Math.ceil(u.ancho / dims.celdaAncho)) : 1,
  spanN: dims.celdaAlto && u.alto > 0 ? Math.max(1, Math.ceil(u.alto / dims.celdaAlto)) : 1,
});

export const cabeEnRack = (u, dims) => {
  const { spanC, spanN } = spanDe(u, dims);
  if (spanC > dims.columnas || spanN > dims.niveles) return false;
  if (dims.celdaProf != null && u.largo > 0 && u.largo > dims.celdaProf) return false;
  return true;
};

//Dibujo del rack como cuadrícula (columna × nivel; profundidad como capa). Cada ubicación se pinta
//ocupando TODAS las celdas que necesita según su tamaño real 
const RackMapaGrid = ({ rack, ubicaciones, ubicacionesSinRack, ubicacionesPorColocar, onAsignar, onQuitar, onDesligar, onCrearEnSlot, onDeshabilitar, onVaciar, puedeModificar = true, ubicacionesDestacadasIds, profundidadInicial }) => {
  const dims = useMemo(() => dimensionesRack(rack), [rack]);
  const [profSel, setProfSel] = useState(profundidadInicial || 1);
  const destacadasSet = useMemo(
    () => new Set((ubicacionesDestacadasIds || []).map((id) => String(id))),
    [ubicacionesDestacadasIds]
  );
  const [modal, setModal] = useState(null); // { columna, nivel, profundidad, ubicacion? }
  const [seleccionadaId, setSeleccionadaId] = useState(undefined);

  const rowOf = (nivel) => dims.niveles - nivel + 1; 

  const disponibles = useMemo(
    () => [...(ubicacionesPorColocar || []), ...(ubicacionesSinRack || [])],
    [ubicacionesPorColocar, ubicacionesSinRack]
  );

  // Ubicaciones colocadas en la capa de profundidad actual, con las celdas que cubren.
  const { items, coveredSet } = useMemo(() => {
    const cubiertas = new Set();
    const lista = [];
    (ubicaciones || [])
      .filter((u) => u.columna && u.nivelRack && (u.profundidad || 1) === profSel)
      .forEach((u) => {
        const { spanC, spanN } = spanDe(u, dims);
        const overflow = u.columna + spanC - 1 > dims.columnas || u.nivelRack + spanN - 1 > dims.niveles;
        const cC = Math.max(1, Math.min(spanC, dims.columnas - u.columna + 1));
        const cN = Math.max(1, Math.min(spanN, dims.niveles - u.nivelRack + 1));
        for (let c = u.columna; c < u.columna + cC; c++) for (let n = u.nivelRack; n < u.nivelRack + cN; n++) cubiertas.add(`${c}-${n}`);
        lista.push({ u, col: u.columna, nivel: u.nivelRack, spanC: cC, spanN: cN, overflow });
      });
    return { items: lista, coveredSet: cubiertas };
  }, [ubicaciones, dims, profSel]);

  const regionLibre = (u, col, nivel) => {
    const { spanC, spanN } = spanDe(u, dims);
    if (col + spanC - 1 > dims.columnas || nivel + spanN - 1 > dims.niveles) return false;
    for (let c = col; c < col + spanC; c++) for (let n = nivel; n < nivel + spanN; n++) if (coveredSet.has(`${c}-${n}`)) return false;
    return true;
  };

  const cerrarModal = () => setModal(null);

  const colocar = (col, nivel, ubicacionId) => {
    const u = disponibles.find((x) => String(x._id) === String(ubicacionId));
    if (!u) return;
    if (!cabeEnRack(u, dims)) { message.error("Esa ubicación es más grande que el rack completo"); return; }
    if (!regionLibre(u, col, nivel)) { message.error("No cabe ahí: se sale o pisa otra ubicación. Prueba otro espacio."); return; }
    onAsignar({ columna: col, nivelRack: nivel, profundidad: profSel }, ubicacionId);
    setSeleccionadaId(undefined);
    cerrarModal();
  };

  const clicEspacioVacio = (col, nivel) => {
    if (!puedeModificar) return;
    if (seleccionadaId) colocar(col, nivel, seleccionadaId);
    else setModal({ columna: col, nivel, profundidad: profSel, ubicacion: null });
  };

  const celdaTexto = () => {
    const p = [];
    if (dims.celdaAncho != null) p.push(`ancho ≤ ${r2(dims.celdaAncho)}m`);
    if (dims.celdaAlto != null) p.push(`alto ≤ ${r2(dims.celdaAlto)}m`);
    if (dims.celdaProf != null) p.push(`largo ≤ ${r2(dims.celdaProf)}m`);
    return p.length ? `${p.join(" · ")} (una ubicación más grande usa varias celdas)` : "Sin dimensiones físicas configuradas";
  };

  const seleccionada = disponibles.find((x) => String(x._id) === String(seleccionadaId));

  // Celdas vacías (no cubiertas), para renderizarlas como "sin asignar".
  const celdasVacias = [];
  for (let c = 1; c <= dims.columnas; c++) for (let n = 1; n <= dims.niveles; n++) if (!coveredSet.has(`${c}-${n}`)) celdasVacias.push({ c, n });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: "#4b5563" }}>Mapa del rack</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {dims.profundidades > 1 && (
            <>
              <span style={{ fontSize: 12, color: "#8a93a6" }}>Profundidad</span>
              <SelectItem width={80} size="small" value={profSel} onChange={setProfSel}>
                {Array.from({ length: dims.profundidades }, (_, i) => i + 1).map((p) => (
                  <Option key={p} value={p}>{p}</Option>
                ))}
              </SelectItem>
            </>
          )}
          {puedeModificar && items.length > 0 && onVaciar && (
            <Popconfirm title="¿Vaciar el acomodo? Las ubicaciones quedan en el rack pero sin espacio (para reacomodar a mano)." okText="Vaciar" cancelText="Cancelar" onConfirm={onVaciar}>
              <ButtonItem danger size="small" icon={<ClearOutlined />}>Vaciar acomodo</ButtonItem>
            </Popconfirm>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 8 }}>Espacio por celda: {celdaTexto()}</div>

      {puedeModificar && (ubicacionesPorColocar || []).length > 0 && (
        <div style={{ marginBottom: 10, padding: 8, background: "#f7f8fa", borderRadius: 6 }}>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6 }}>Por colocar — elige una y luego un espacio en blanco:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(ubicacionesPorColocar || []).map((u) => {
              const sel = String(u._id) === String(seleccionadaId);
              const cabe = cabeEnRack(u, dims);
              return (
                <Tag
                  key={u._id}
                  color={sel ? "blue" : cabe ? "default" : "red"}
                  style={{ cursor: "pointer", padding: "2px 8px", fontSize: 12 }}
                  onClick={() => setSeleccionadaId(sel ? undefined : u._id)}
                  closable={!!onDesligar}
                  onClose={(e) => { e.preventDefault(); if (onDesligar) onDesligar(u._id); }}
                  closeIcon={<span title="Desligar del rack" style={{ marginLeft: 4 }}>✕</span>}
                >
                  {u.clave}{medidasTexto(u) ? ` · ${medidasTexto(u)}` : ""}{!cabe ? " · no cabe" : ""}
                </Tag>
              );
            })}
          </div>
          {seleccionada && (
            <div style={{ fontSize: 11, color: "#1677ff", marginTop: 6 }}>
              Ocupa {spanDe(seleccionada, dims).spanC}×{spanDe(seleccionada, dims).spanN} celda(s). Haz clic en un espacio resaltado para colocarla.
            </div>
          )}
        </div>
      )}


      <div
        style={{
          display: "grid",
          gridTemplateColumns: `34px repeat(${dims.columnas}, minmax(48px, 1fr))`,
          gridTemplateRows: `repeat(${dims.niveles}, 64px)`,
          gap: 6,
        }}
      >
        {/* Etiquetas de nivel */}
        {Array.from({ length: dims.niveles }, (_, i) => i + 1).map((nivel) => (
          <div key={`lbl-${nivel}`} style={{ gridColumn: 1, gridRow: rowOf(nivel), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#8a93a6" }}>
            N{nivel}
          </div>
        ))}

        {/* Espacios vacíos */}
        {celdasVacias.map(({ c, n }) => {
          const resaltar = seleccionada && regionLibre(seleccionada, c, n);
          return (
            <div
              key={`vac-${c}-${n}`}
              onClick={() => clicEspacioVacio(c, n)}
              title={resaltar ? "Clic para colocar aquí" : "Sin asignar"}
              style={{
                gridColumn: c + 1,
                gridRow: rowOf(n),
                border: resaltar ? "2px dashed #1677ff" : "1px dashed #cbd2e0",
                borderRadius: 6,
                background: resaltar ? "#eef5ff" : "#fafbfc",
                cursor: "pointer",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 10, color: "#b7bfce" }}>sin asignar</span>
              <span style={{ position: "absolute", top: 2, right: 4, fontSize: 9, color: "#c3c9d4" }}>C{c}</span>
            </div>
          );
        })}

        {items.map(({ u, col, nivel, spanC, spanN, overflow }) => {
          const destacada = destacadasSet.has(String(u._id));
          return (
            <div
              key={`ub-${u._id}`}
              onClick={() => setModal({ columna: col, nivel, profundidad: profSel, ubicacion: u })}
              title={`${u.clave}${destacada ? " (destino)" : ""}${overflow ? " (no cabe con la configuración actual)" : ""}`}
              style={{
                gridColumn: `${col + 1} / span ${spanC}`,
                gridRow: `${rowOf(nivel + spanN - 1)} / span ${spanN}`,
                border: destacada ? "2px solid #1677ff" : overflow ? "2px solid #e5484d" : "1px solid #d9a441",
                background: destacada ? "#d6e8ff" : overflow ? "#ffccc7" : u.insumos?.length ? "#f6c945" : "#f4e6c3",
                borderRadius: 6,
                cursor: "pointer",
                padding: "4px 6px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                boxShadow: destacada ? "0 0 0 2px #1677ff33" : undefined,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: destacada ? "#0958d9" : overflow ? "#a8071a" : "#5c4a12" }}>
                {u.clave}
              </div>
              <div style={{ fontSize: 10, color: destacada ? "#1677ff" : overflow ? "#cf1322" : "#6b5b1e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {destacada ? "Aquí se va a guardar" : overflow ? "no cabe aquí" : medidasTexto(u) || `${u.insumos?.length || 0} insumo(s)`}
              </div>
            </div>
          );
        })}
      </div>

      <RackEspacioModal
        modal={modal}
        onCancel={cerrarModal}
        profundidades={dims.profundidades}
        disponibles={disponibles}
        regionLibre={regionLibre}
        medidasTexto={medidasTexto}
        puedeModificar={puedeModificar}
        puedeDeshabilitar={!!onDeshabilitar}
        onColocar={(ubicacionId) => colocar(modal.columna, modal.nivel, ubicacionId)}
        onQuitarAcomodo={() => { onQuitar(modal.ubicacion._id); cerrarModal(); }}
        onDesligar={() => { if (onDesligar) onDesligar(modal.ubicacion._id); cerrarModal(); }}
        onDeshabilitar={() => { if (onDeshabilitar) onDeshabilitar(modal.ubicacion._id); cerrarModal(); }}
        onCrear={onCrearEnSlot ? () => { onCrearEnSlot({ columna: modal.columna, nivelRack: modal.nivel, profundidad: modal.profundidad }); cerrarModal(); } : undefined}
      />
    </div>
  );
};

export default RackMapaGrid;
